import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import { cn } from '../../lib/cn.js';

/**
 * Botao do design system Rimati (Apendice H.6).
 * Aparencia default de biblioteca nao e aceita como final: as variantes abaixo
 * usam os tokens do produto.
 *
 * Botao desabilitado nao explica o motivo. Quando o motivo nao for obvio, a tela
 * deve usar `aria-disabled="true"` (mantem o botao focavel) junto de uma dica
 * textual visivel ligada por `aria-describedby`. O comportamento de `disabled`
 * aqui nao muda (afetaria muitas telas).
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  loading?: boolean;
  ref?: Ref<HTMLButtonElement>;
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-brand text-white shadow-panel hover:bg-brand-hover active:bg-brand-hover ' +
    'disabled:bg-brand/45 disabled:shadow-none',
  secondary:
    'bg-surface text-ink border border-line hover:bg-surface-sunken hover:border-line-strong ' +
    'disabled:!text-ink disabled:!bg-surface disabled:hover:!bg-surface',
  ghost:
    'bg-transparent text-ink-soft hover:bg-surface-sunken hover:text-ink disabled:text-ink-faint',
  danger: 'bg-danger text-white hover:brightness-95 disabled:bg-danger/45',
};

/**
 * FINAL POLISH (seções 2 e 32) - a área de toque cresce; o botão, não.
 *
 * O botão compacto tem 32px de altura, e 32px é pouco para um dedo. Aumentar a
 * altura visual resolveria — e reabriria dez layouts já aprovados, porque esses
 * botões dividem linha com nomes, valores e selos que foram medidos em cima
 * dessa altura.
 *
 * O `::before` estende a área clicável 6px para cima e 6px para baixo, chegando
 * aos 44px, sem ocupar espaço no fluxo: pseudo-elemento absoluto não empurra
 * nada. O visual continua idêntico ao aprovado, e o dedo passa a acertar.
 *
 * SÓ NA VERTICAL, e de propósito. Na horizontal os botões compactos costumam
 * ficar lado a lado com `gap-2` (8px), e esticar 6px para cada lado faria as
 * áreas de dois vizinhos se encostarem — o clique de um cairia no outro. Um
 * teste mede exatamente isso nas telas onde eles existem.
 */
const SIZES: Record<ButtonSize, string> = {
  sm:
    'h-8 px-3 text-body gap-1.5 relative ' +
    'before:absolute before:inset-x-0 before:-inset-y-[6px] before:content-[""]',
  md: 'min-h-11 px-4 py-2 text-sm gap-2 sm:min-h-10',
};

export function Button({
  variant = 'primary',
  size = 'md',
  icon,
  loading = false,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps): ReactNode {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex max-w-full shrink-0 cursor-pointer items-center justify-center rounded-control text-center font-medium whitespace-normal',
        rest['aria-pressed'] !== undefined
          ? 'transition-shadow duration-[160ms]'
          : 'transition-[background-color,border-color,color,box-shadow] duration-[160ms]',
        'disabled:cursor-not-allowed',
        SIZES[size],
        VARIANTS[variant],
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner /> : icon}
      {children}
    </button>
  );
}

function Spinner(): ReactNode {
  return (
    <span
      aria-hidden="true"
      className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent opacity-70"
    />
  );
}
